/**
 * Settings Feature Constants
 * All hard-coded values, configuration options, and enums for the settings feature
 */

// ============================================================================
// SETTINGS CATEGORIES
// ============================================================================

/**
 * Settings category options
 * Use translation keys for labels: t(`settings.category.${value}`)
 */
export const SETTINGS_CATEGORIES = [
  { value: "general", label: "settings.category.general" },
  { value: "profile", label: "settings.category.profile" },
  { value: "notifications", label: "settings.category.notifications" },
  { value: "security", label: "settings.category.security" },
  { value: "billing", label: "settings.category.billing" },
  { value: "api", label: "settings.category.api" },
  { value: "appearance", label: "settings.category.appearance" },
];

// ============================================================================
// NOTIFICATION TYPES
// ============================================================================

/**
 * Notification type options
 * Use translation keys for labels: t(`settings.notifications.${value}`)
 */
export const NOTIFICATION_TYPES = {
  EMAIL: "email",
  SMS: "sms",
  PUSH: "push",
  IN_APP: "inApp",
};

/**
 * Notification event options
 */
export const NOTIFICATION_EVENTS = [
  { value: "package_delivered", label: "settings.notifications.packageDelivered" },
  { value: "package_status_changed", label: "settings.notifications.packageStatusChanged" },
  { value: "invoice_generated", label: "settings.notifications.invoiceGenerated" },
  { value: "payment_received", label: "settings.notifications.paymentReceived" },
  { value: "ticket_updated", label: "settings.notifications.ticketUpdated" },
  { value: "pickup_scheduled", label: "settings.notifications.pickupScheduled" },
];

// ============================================================================
// LANGUAGE OPTIONS
// ============================================================================

/**
 * Available language options
 */
export const LANGUAGE_OPTIONS = [
  { value: "en", label: "English", flag: "🇬🇧" },
  { value: "fr", label: "Français", flag: "🇫🇷" },
  { value: "ar", label: "العربية", flag: "🇲🇦" },
];

// ============================================================================
// THEME OPTIONS
// ============================================================================

/**
 * Theme options
 * Use translation keys for labels: t(`settings.theme.${value}`)
 */
export const THEME_OPTIONS = [
  { value: "light", label: "settings.theme.light" },
  { value: "dark", label: "settings.theme.dark" },
  { value: "system", label: "settings.theme.system" },
];

// ============================================================================
// CURRENCY OPTIONS
// ============================================================================

/**
 * Currency options
 */
export const CURRENCY_OPTIONS = [
  { value: "MAD", label: "MAD - Moroccan Dirham", symbol: "DH" },
  { value: "USD", label: "USD - US Dollar", symbol: "$" },
  { value: "EUR", label: "EUR - Euro", symbol: "€" },
];

// ============================================================================
// DATE FORMAT OPTIONS
// ============================================================================

/**
 * Date format options
 */
export const DATE_FORMAT_OPTIONS = [
  { value: "DD/MM/YYYY", label: "DD/MM/YYYY" },
  { value: "MM/DD/YYYY", label: "MM/DD/YYYY" },
  { value: "YYYY-MM-DD", label: "YYYY-MM-DD" },
  { value: "DD.MM.YYYY", label: "DD.MM.YYYY" },
];

// ============================================================================
// TIME FORMAT OPTIONS
// ============================================================================

/**
 * Time format options
 */
export const TIME_FORMAT_OPTIONS = [
  { value: "12h", label: "12-hour (AM/PM)" },
  { value: "24h", label: "24-hour" },
];

// ============================================================================
// DEFAULT VALUES
// ============================================================================

/**
 * Default values for settings
 */
export const SETTINGS_DEFAULT_VALUES = {
  DELIVERY_FEES: 30,
  REFUSAL_FEES: 10,
  CANCELLATION_FEES: 5,
  DEADLINE: 7,
  LANGUAGE: 'en',
  THEME: 'light',
  TIMEZONE: 'UTC',
};

// ============================================================================
// FORM FIELD NAMES
// ============================================================================

/**
 * Settings form field names
 */
export const SETTINGS_FORM_FIELDS = {
  // General
  LANGUAGE: "language",
  TIMEZONE: "timezone",
  CURRENCY: "currency",
  DATE_FORMAT: "dateFormat",
  TIME_FORMAT: "timeFormat",
  
  // Profile
  FULL_NAME: "fullName",
  EMAIL: "email",
  PHONE: "phone",
  BRAND_NAME: "brandName",
  WEBSITE: "website",
  ADDRESS: "address",
  CITY: "city",
  
  // Notifications
  EMAIL_NOTIFICATIONS: "emailNotifications",
  SMS_NOTIFICATIONS: "smsNotifications",
  PUSH_NOTIFICATIONS: "pushNotifications",
  
  // Security
  TWO_FACTOR_AUTH: "twoFactorAuth",
  OLD_PASSWORD: "oldPassword",
  NEW_PASSWORD: "newPassword",
  CONFIRM_PASSWORD: "confirmPassword",
  
  // Appearance
  THEME: "theme",
  COMPACT_MODE: "compactMode",
};

// ============================================================================
// VALIDATION RULES
// ============================================================================

/**
 * Settings validation constants
 */
export const SETTINGS_VALIDATION = {
  PASSWORD_MIN_LENGTH: 8,
  PHONE_MIN_LENGTH: 10,
  PHONE_MAX_LENGTH: 10,
};

// ============================================================================
// TOAST MESSAGE KEYS
// ============================================================================

/**
 * Toast message translation keys for settings operations
 */
export const SETTINGS_TOAST_KEYS = {
  SAVING: "toast.savingSettings",
  SAVE_SUCCESS: "toast.settingsSaved",
  SAVE_FAILED: "toast.settingsSaveFailed",
  PASSWORD_CHANGED: "settings.passwordChanged",
  PASSWORD_CHANGE_FAILED: "settings.passwordChangeFailed",
  PROFILE_UPDATED: "settings.profileUpdated",
  NOTIFICATIONS_UPDATED: "settings.notificationsUpdated",
  PRICING_CREATED: "Custom pricing created successfully",
  PRICING_CREATE_ERROR: "Failed to create custom pricing",
  PRICING_UPDATED: "Custom pricing updated successfully",
  PRICING_UPDATE_ERROR: "Failed to update custom pricing",
  PRICING_DELETED: "Custom pricing deleted successfully",
  PRICING_DELETE_ERROR: "Failed to delete custom pricing",
};

// ============================================================================
// ROUTE PATHS
// ============================================================================

/**
 * Settings-related route paths
 */
export const SETTINGS_ROUTES = {
  BASE: "/settings",
  GENERAL: "/settings/general",
  PROFILE: "/settings/profile",
  NOTIFICATIONS: "/settings/notifications",
  SECURITY: "/settings/security",
  BILLING: "/settings/billing",
  API: "/settings/api",
  APPEARANCE: "/settings/appearance",
};

// ============================================================================
// QUERY KEYS
// ============================================================================

/**
 * React Query keys for settings-related queries
 */
export const SETTINGS_QUERY_KEYS = {
  BASE: "settings",
  GENERAL: "settings-general",
  PROFILE: "settings-profile",
  NOTIFICATIONS: "settings-notifications",
  SECURITY: "settings-security",
  PREFERENCES: "user-preferences",
};

// ============================================================================
// LOCAL STORAGE KEYS
// ============================================================================

/**
 * Local storage keys for settings
 */
export const SETTINGS_STORAGE_KEYS = {
  LANGUAGE: "user-language",
  THEME: "user-theme",
  CURRENCY: "user-currency",
  DATE_FORMAT: "user-date-format",
  TIME_FORMAT: "user-time-format",
  COMPACT_MODE: "compact-mode",
};

// ============================================================================
// DEFAULT VALUES
// ============================================================================

/**
 * Default settings values
 */
export const DEFAULT_SETTINGS = {
  language: "en",
  timezone: "Africa/Casablanca",
  currency: "MAD",
  dateFormat: "DD/MM/YYYY",
  timeFormat: "24h",
  theme: "system",
  emailNotifications: true,
  smsNotifications: false,
  pushNotifications: true,
  compactMode: false,
};
